import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { mapProps } from 'recompose';
import { connect } from 'react-redux';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';

import {
  PhotoSearchCheckoutSuccessModal,
  PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL,
} from '../../../../apps/temp/views/onboarding/PhotoSearchCheckout/components/PhotoSearchCheckoutSuccess';
import usePhotoSearchCheckout from '../../../../apps/temp/views/onboarding/PhotoSearchCheckout/hooks/usePhotoSearchCheckout';
import BoxedItem from '../../../global/BoxedItem';
import { getErrorString } from '../../../../util/normalize-errors';
import getPhotoSearchCheckoutSearchItems from '../../../../apps/temp/views/onboarding/PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import { AccountPhotoSearchCheckout } from '../../../../graphql/queries/account';
import {
  openAlert as openAlertAction,
  openModal as openModalAction,
  closeModal as closeModalAction,
} from '../../../../actions/modal-actions';
import { AlbumFolderWithCreatorDetails } from '../../../../graphql/queries/album-folder';
import Placeholder from '../../../../apps/temp/views/onboarding/PhotoSearchUpgradeCheckout/Placeholder';
import PhotoSearchUpsellSubscriptionSelection from '../../../../apps/temp/views/onboarding/PhotoSearchUpsell/components/PhotoSearchUpsellSubscriptionSelection/PhotoSearchUpsellSubscriptionSelection';
import WaldoSpeechBubble from '../../../global/WaldoSpeechBubble';
import Button from '../../../global/Button';
import getConfig from '../../../../util/config';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  searchSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      plan: PropTypes.string.isRequired,
      days: PropTypes.number.isRequired,
      price: PropTypes.shape({
        value: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ),
  openAlert: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  refetchAccountSearchSubscriptions: PropTypes.func.isRequired,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  loadingAlbumFolder: PropTypes.bool,
};

const AlbumFolderPurchaseShutterbugUpsell = ({
  account,
  searchSubscriptionItems,
  openAlert,
  openModal,
  closeModal,
  refetchAccountSearchSubscriptions,
  albumFolder,
  loadingAlbumFolder,
}) => {
  const [selectedSearchItem, setSelectedSearchItem] = useState(null);
  const { branch, marcomUrl } = getConfig();

  const { submitting, onCheckout } = usePhotoSearchCheckout({
    account,
    selectedSearchItem,
    searchSubscriptionItems,
    onCheckoutError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    onCheckoutSuccess: async () => {
      await refetchAccountSearchSubscriptions();
      openModal(PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL, {
        content: (
          <div className="text-center">
            <p className="text-bold mb-3">
              I&rsquo;ll notify you when I find photos you&rsquo;re in!
            </p>

            <BoxedItem className="photo-search-checkout-success-create-box">
              <p className="text-bold">Have photos to share?</p>

              <div className="d-flex flex-column">
                <Button
                  href={`${marcomUrl}/search`}
                  target="_blank"
                  rel="noopener"
                  btnStyle="yellow"
                  btnSize="lg"
                  className="text-black px-3 py-2 mb-1"
                  onClick={() => {
                    closeModal(PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL);
                  }}
                >
                  Find the gallery for your event
                </Button>
                <Button
                  href={branch?.app}
                  btnStyle="yellow"
                  btnSize="lg"
                  className="text-black text-black px-3 py-2"
                  onClick={() => {
                    closeModal(PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL);
                  }}
                >
                  Create your own gallery
                </Button>
              </div>
            </BoxedItem>
          </div>
        ),
      });
    },
  });

  if (
    !account ||
    !searchSubscriptionItems ||
    loadingAlbumFolder ||
    submitting
  ) {
    return <Placeholder />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Get your search subscription! &ndash; Waldo Photos</title>
      </Helmet>

      <WaldoSpeechBubble position="right" className="mb-2">
        You have successfully joined{' '}
        <span className="text-primary">{albumFolder?.name}</span>!
      </WaldoSpeechBubble>

      <p className="text-center">
        Alert! Some albums in this gallery require a Waldo Subscription for
        photo matching. To get all your photos, sign-up for a Waldo Subscription
        below:
      </p>

      <PhotoSearchUpsellSubscriptionSelection
        searchSubscriptionItems={searchSubscriptionItems}
        onSelect={setSelectedSearchItem}
        selectedSearchItem={selectedSearchItem}
        onCheckout={onCheckout}
        account={account}
      />

      <PhotoSearchCheckoutSuccessModal />
    </Fragment>
  );
};

AlbumFolderPurchaseShutterbugUpsell.propTypes = propTypes;

const withAccount = graphql(AccountPhotoSearchCheckout, {
  props: ({ data: { me, refetch } = {} }) => ({
    account: me,
    refetchAccountSearchSubscriptions: refetch,
  }),
  options: {
    fetchPolicy: 'cache-and-network',
  },
});

const withAlbumFolderData = graphql(AlbumFolderWithCreatorDetails, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    albumFolder,
    loadingAlbumFolder: loading,
  }),
});

const mapDispatchToProps = {
  openAlert: openAlertAction,
  openModal: openModalAction,
  closeModal: closeModalAction,
};

export { AlbumFolderPurchaseShutterbugUpsell as AlbumFolderPurchaseShutterbugUpsellComponent };

export default compose(
  connect(null, mapDispatchToProps),
  withAccount,
  getPhotoSearchCheckoutSearchItems,
  mapProps(({ data, ...props }) => ({
    ...props,
    searchSubscriptionItems: data?.searchSubscriptionItems,
  })),
  withAlbumFolderData,
)(AlbumFolderPurchaseShutterbugUpsell);
